import React, { useState } from 'react';
import styled from 'styled-components';
import { RotateCcw, Package, Clock, CheckCircle, AlertTriangle, Search, Filter, Calendar, Phone, FileText } from 'lucide-react';
import ContentLayout from '../../../Common/Component/ContentLayout';

const Container = styled.div`
  width: 100%;
`;

const TabContainer = styled.div`
  background: white;
  border-radius: 8px 8px 0 0;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 0;
`;

const TabList = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e7eb;
`;

const Tab = styled.button`
  flex: 1;
  padding: 15px 20px;
  border: none;
  background: ${props => props.active ? '#3b82f6' : 'white'};
  color: ${props => props.active ? 'white' : '#6b7280'};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:first-child {
    border-radius: 8px 0 0 0;
  }
  
  &:last-child {
    border-radius: 0 8px 0 0;
  }
  
  &:hover:not(:disabled) {
    background: ${props => props.active ? '#2563eb' : '#f3f4f6'};
  }
`;

const ContentSection = styled.div`
  background: white;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  padding: 20px;
`;

const FilterBar = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  min-width: 120px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  min-width: 200px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const ReturnsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const ReturnItem = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
`;

const ItemHeader = styled.div`
  padding: 20px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
`;

const ItemInfo = styled.div`
  flex: 1;
`;

const RequestNumber = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 5px 0;
`;

const ProductName = styled.p`
  font-size: 14px;
  color: #374151;
  margin: 0 0 5px 0;
`;

const RequestDate = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 0;
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch(props.status) {
      case 'requested': return '#fef3c7';
      case 'approved': return '#dbeafe';
      case 'pickup-scheduled': return '#e0f2fe';
      case 'in-process': return '#e0f2fe';
      case 'completed': return '#dcfce7';
      case 'cancelled': return '#fee2e2';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'requested': return '#d97706';
      case 'approved': return '#1d4ed8';
      case 'pickup-scheduled': return '#0369a1';
      case 'in-process': return '#0369a1';
      case 'completed': return '#15803d';
      case 'cancelled': return '#dc2626';
      default: return '#6b7280';
    }
  }};
`;

const ItemDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
`;

const DetailGroup = styled.div``;

const DetailLabel = styled.p`
  font-size: 11px;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  margin: 0 0 5px 0;
`;

const DetailValue = styled.p`
  font-size: 13px;
  color: #1f2937;
  margin: 0;
  font-weight: 500;
`;

const ItemContent = styled.div`
  padding: 20px;
`;

const ProgressSection = styled.div`
  margin-bottom: 20px;
`;

const ProgressTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 15px 0;
`;

const ProgressSteps = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  overflow-x: auto;
  padding: 10px 0;
`;

const ProgressStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 80px;
  text-align: center;
`;

const StepIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.active ? '#3b82f6' : props.completed ? '#10b981' : '#e5e7eb'};
  color: ${props => (props.active || props.completed) ? 'white' : '#9ca3af'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
`;

const StepLabel = styled.div`
  font-size: 11px;
  color: ${props => props.active ? '#3b82f6' : props.completed ? '#10b981' : '#6b7280'};
  font-weight: 500;
`;

const StepConnector = styled.div`
  flex: 1;
  height: 2px;
  background: ${props => props.completed ? '#10b981' : '#e5e7eb'};
  margin: 0 10px;
`;

const ReasonSection = styled.div`
  background: #f9fafb;
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 15px;
`;

const ReasonTitle = styled.h5`
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 5px 0;
  text-transform: uppercase;
`;

const ReasonText = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 0;
  line-height: 1.4;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.primary ? '#3b82f6' : 'white'};
  color: ${props => props.primary ? 'white' : '#374151'};
  border: ${props => props.primary ? 'none' : '1px solid #d1d5db'};
  
  &:hover {
    background: ${props => props.primary ? '#2563eb' : '#f3f4f6'};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: #9ca3af;
`;

function ReturnsTracking() {
  const [activeTab, setActiveTab] = useState('returns');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // 더미 반품/교환 데이터
  const returnsData = [
    {
      id: 1,
      type: 'return',
      requestNumber: 'RT-2024-001',
      originalOrder: 'ORD-2024-001',
      trackingNumber: 'TK123456789',
      productName: '프리미엄 무선 헤드폰',
      status: 'in-process',
      statusText: '처리중',
      requestDate: '2024-02-14',
      reason: '상품불량',
      reasonDetail: '좌측 헤드폰에서 잡음이 발생합니다.',
      pickupDate: '2024-02-16',
      completionDate: null,
      refundAmount: 299000,
      progress: {
        requested: true,
        approved: true,
        pickupScheduled: true,
        inProcess: true,
        completed: false
      }
    },
    {
      id: 2,
      type: 'exchange',
      requestNumber: 'EX-2024-001',
      originalOrder: 'ORD-2024-002',
      trackingNumber: 'TK987654321',
      productName: '스마트워치 시리즈 9 (블랙)',
      status: 'completed',
      statusText: '완료',
      requestDate: '2024-02-10',
      reason: '색상변경',
      reasonDetail: '블랙에서 화이트로 변경 요청',
      pickupDate: '2024-02-12',
      completionDate: '2024-02-15',
      exchangeProduct: '스마트워치 시리즈 9 (화이트)',
      progress: {
        requested: true,
        approved: true,
        pickupScheduled: true,
        inProcess: true,
        completed: true
      }
    },
    {
      id: 3,
      type: 'return',
      requestNumber: 'RT-2024-002',
      originalOrder: 'ORD-2024-003',
      trackingNumber: 'TK456789123',
      productName: '블루투스 이어폰',
      status: 'requested',
      statusText: '신청',
      requestDate: '2024-02-15',
      reason: '단순변심',
      reasonDetail: '더 나은 제품을 찾아서',
      pickupDate: null,
      completionDate: null,
      refundAmount: 199000,
      progress: {
        requested: true,
        approved: false,
        pickupScheduled: false,
        inProcess: false,
        completed: false
      }
    }
  ];

  const filteredData = returnsData.filter(item => {
    const matchesTab = activeTab === 'all' || 
      (activeTab === 'returns' && item.type === 'return') ||
      (activeTab === 'exchanges' && item.type === 'exchange');
    
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    
    const matchesSearch = searchTerm === '' ||
      item.requestNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.originalOrder.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesTab && matchesStatus && matchesSearch;
  });

  const getStatusIcon = (status) => {
    switch(status) {
      case 'requested': return <Clock size={14} />;
      case 'approved': return <CheckCircle size={14} />;
      case 'in-process': return <Package size={14} />;
      case 'completed': return <CheckCircle size={14} />;
      case 'cancelled': return <AlertTriangle size={14} />;
      default: return <Package size={14} />;
    }
  };

  const getProgressSteps = (type) => {
    if (type === 'return') {
      return [
        { key: 'requested', label: '신청' },
        { key: 'approved', label: '승인' },
        { key: 'pickupScheduled', label: '수거예약' },
        { key: 'inProcess', label: '처리중' },
        { key: 'completed', label: '완료' }
      ];
    } else {
      return [
        { key: 'requested', label: '신청' },
        { key: 'approved', label: '승인' },
        { key: 'pickupScheduled', label: '수거예약' },
        { key: 'inProcess', label: '교환처리' },
        { key: 'completed', label: '완료' }
      ];
    }
  };

  return (
    <ContentLayout 
      title="반품/교환 추적"
      subtitle="반품 및 교환 요청의 처리 현황을 실시간으로 확인하실 수 있습니다"
    >
      <Container>
        <TabContainer>
          <TabList>
            <Tab 
              active={activeTab === 'all'} 
              onClick={() => setActiveTab('all')}
            >
              <FileText size={16} />
              전체
            </Tab>
            <Tab 
              active={activeTab === 'returns'} 
              onClick={() => setActiveTab('returns')}
            >
              <RotateCcw size={16} />
              반품
            </Tab>
            <Tab 
              active={activeTab === 'exchanges'} 
              onClick={() => setActiveTab('exchanges')}
            >
              <Package size={16} />
              교환
            </Tab>
          </TabList>
        </TabContainer>

        <ContentSection>
          <FilterBar>
            <FilterSelect 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">전체 상태</option>
              <option value="requested">신청</option>
              <option value="approved">승인</option>
              <option value="in-process">처리중</option>
              <option value="completed">완료</option>
              <option value="cancelled">취소</option>
            </FilterSelect>

            <SearchInput
              type="text"
              placeholder="요청번호, 상품명, 주문번호 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </FilterBar>

          {filteredData.length > 0 ? (
            <ReturnsList>
              {filteredData.map(item => (
                <ReturnItem key={item.id}>
                  <ItemHeader>
                    <HeaderTop>
                      <ItemInfo>
                        <RequestNumber>
                          {item.requestNumber} ({item.type === 'return' ? '반품' : '교환'})
                        </RequestNumber>
                        <ProductName>{item.productName}</ProductName>
                        <RequestDate>신청일: {item.requestDate}</RequestDate>
                      </ItemInfo>
                      <StatusBadge status={item.status}>
                        {getStatusIcon(item.status)}
                        {item.statusText}
                      </StatusBadge>
                    </HeaderTop>
                    
                    <ItemDetails>
                      <DetailGroup>
                        <DetailLabel>원주문번호</DetailLabel>
                        <DetailValue>{item.originalOrder}</DetailValue>
                      </DetailGroup>
                      <DetailGroup>
                        <DetailLabel>운송장번호</DetailLabel>
                        <DetailValue>{item.trackingNumber}</DetailValue>
                      </DetailGroup>
                      <DetailGroup>
                        <DetailLabel>수거예정일</DetailLabel>
                        <DetailValue>{item.pickupDate || '미정'}</DetailValue>
                      </DetailGroup>
                      <DetailGroup>
                        <DetailLabel>{item.type === 'return' ? '환불예정금액' : '교환상품'}</DetailLabel>
                        <DetailValue>
                          {item.type === 'return' 
                            ? `${item.refundAmount?.toLocaleString()}원`
                            : item.exchangeProduct
                          }
                        </DetailValue>
                      </DetailGroup>
                    </ItemDetails>
                  </ItemHeader>

                  <ItemContent>
                    <ProgressSection>
                      <ProgressTitle>진행 상황</ProgressTitle>
                      <ProgressSteps>
                        {getProgressSteps(item.type).map((step, index, array) => (
                          <React.Fragment key={step.key}>
                            <ProgressStep>
                              <StepIcon 
                                completed={item.progress[step.key]} 
                                active={!item.progress[step.key] && index > 0 && item.progress[array[index-1]?.key]}
                              >
                                {item.progress[step.key] ? <CheckCircle size={16} /> : <Clock size={16} />}
                              </StepIcon>
                              <StepLabel 
                                completed={item.progress[step.key]}
                                active={!item.progress[step.key] && index > 0 && item.progress[array[index-1]?.key]}
                              >
                                {step.label}
                              </StepLabel>
                            </ProgressStep>
                            {index < array.length - 1 && (
                              <StepConnector completed={item.progress[array[index + 1]?.key]} />
                            )}
                          </React.Fragment>
                        ))}
                      </ProgressSteps>
                    </ProgressSection>

                    <ReasonSection>
                      <ReasonTitle>{item.type === 'return' ? '반품' : '교환'} 사유</ReasonTitle>
                      <ReasonText><strong>{item.reason}</strong> - {item.reasonDetail}</ReasonText>
                    </ReasonSection>

                    <ActionButtons>
                      <ActionButton>상세보기</ActionButton>
                      {item.status === 'requested' && (
                        <ActionButton primary>수정하기</ActionButton>
                      )}
                      {item.status !== 'completed' && item.status !== 'cancelled' && (
                        <ActionButton>취소하기</ActionButton>
                      )}
                    </ActionButtons>
                  </ItemContent>
                </ReturnItem>
              ))}
            </ReturnsList>
          ) : (
            <EmptyState>
              <EmptyIcon>
                <RotateCcw size={40} />
              </EmptyIcon>
              <h3 style={{margin: '0 0 10px 0', color: '#374151'}}>
                {activeTab === 'returns' ? '반품' : activeTab === 'exchanges' ? '교환' : '반품/교환'} 내역이 없습니다
              </h3>
              <p style={{margin: 0}}>
                검색 조건을 변경하거나 새로운 요청을 등록해보세요
              </p>
            </EmptyState>
          )}
        </ContentSection>
      </Container>
    </ContentLayout>
  );
}

export default ReturnsTracking;